import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle2,
  Check,
  ArrowRight,
  User,
  Mic,
  Palette,
  Sliders,
  Film
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, ROLE_LABELS, ROLE_ICON_COMPONENTS } from '../../lib/rolePermissions';
import { validatePassword } from '../../lib/passwordValidator';
import EmailVerificationModal from './EmailVerificationModal';
import SEO from '../common/SEO';

// Authoritative signup assets per reference 02.webp
import upperBookmarkLogo from '../../assets/signup/upper-bookmark-logo.webp';
import sheetDownElement from '../../assets/signup/signup-sheet-down-element.webp';

import '../../styles/SignupPage.css';

// 5 Official SenritsuX Creator Professions
const AVAILABLE_ROLES_ROW_1 = [
  ROLES.SINGER,
  ROLES.RAPPER,
  ROLES.GRAPHIC_DESIGNER,
];

const AVAILABLE_ROLES_ROW_2 = [
  ROLES.PRODUCER,
  ROLES.VIDEO_EDITOR,
];

// Fallback icon resolver for roles
const getRoleIcon = (role) => {
  if (role === ROLES.SINGER) return Mic;
  if (role === ROLES.RAPPER) return Mic;
  if (role === ROLES.GRAPHIC_DESIGNER) return Palette;
  if (role === ROLES.PRODUCER) return Sliders;
  if (role === ROLES.VIDEO_EDITOR) return Film;
  return ROLE_ICON_COMPONENTS[role] || User;
};

// 8 Independent Password Checklist Items
const PASSWORD_CRITERIA = [
  { key: 'minLength', label: 'At least 8 characters' },
  { key: 'hasUppercase', label: 'At least one uppercase letter (A-Z)' },
  { key: 'hasLowercase', label: 'At least one lowercase letter (a-z)' },
  { key: 'hasNumber', label: 'At least one number (0-9)' },
  { key: 'hasSpecialChar', label: 'At least one special character (!@#$...)' },
  { key: 'hasNoEmoji', label: 'No emoji characters' },
  { key: 'hasNoUserInfo', label: 'Does not contain your name or email' },
  { key: 'isNotCommon', label: 'Not a common or easily guessed password' },
];

export default function SignupPage() {
  const [fullName, setFullName] = useState('');
  const [stageName, setStageName] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Focus tracking for password checklist panel
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isPanelFocused, setIsPanelFocused] = useState(false);
  const panelRef = useRef(null);

  const [primaryRole, setPrimaryRole] = useState('');
  const [additionalRoles, setAdditionalRoles] = useState([]);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  const isSubmittingRef = useRef(false);

  const {
    user,
    profile,
    roles,
    signUp,
    createProfile,
    completeOnboarding,
    checkEmailExists,
    isConfigured,
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const isGoogleUser = !!user;

  // Single Source of Truth: Derived live validation for panel & form
  const passwordValidation = useMemo(() => {
    return validatePassword(password, {
      email: email.trim(),
      fullName: fullName.trim(),
      stageName: stageName.trim(),
    });
  }, [password, email, fullName, stageName]);

  const showPasswordPanel = isPasswordFocused || isPanelFocused;

  // Dismiss privacy consent banner on dedicated auth page
  useEffect(() => {
    try {
      if (!localStorage.getItem('senritsux_analytics_consent')) {
        localStorage.setItem('senritsux_analytics_consent', 'accepted');
      }
    } catch {}
  }, []);

  // Pre-fill metadata if user is authenticated via Google
  useEffect(() => {
    if (user) {
      if (profile?.onboarding_completed) {
        navigate('/dashboard', { replace: true });
        return;
      }
      const meta = user.user_metadata || {};
      setFullName((prev) => prev || profile?.name || meta.full_name || meta.name || '');
      setStageName((prev) => prev || profile?.stage_name || meta.stage_name || '');
      setEmail(user.email || '');
      setPhone((prev) => prev || profile?.phone || meta.phone || '');
      setCity((prev) => prev || profile?.city || meta.city || '');
      if (roles && roles.length > 0) {
        setPrimaryRole((prev) => prev || roles[0]);
        setAdditionalRoles((prev) => (prev.length === 0 ? roles.slice(1) : prev));
      } else if (meta.primary_role) {
        setPrimaryRole((prev) => prev || meta.primary_role);
        if (meta.additional_roles) {
          setAdditionalRoles((prev) => (prev.length === 0 ? meta.additional_roles : prev));
        }
      }
    }
  }, [user, profile, roles, navigate]);

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const validatePhone = (val) => {
    if (!val) return true; // Phone is optional
    return /^(\+?\d{1,4}[\s-]?)?\d{7,14}$/.test(val.replace(/[\s-]/g, ''));
  };

  const handleRoleClick = (role) => {
    if (!primaryRole) {
      setPrimaryRole(role);
      if (fieldErrors.role) {
        setFieldErrors((prev) => ({ ...prev, role: null }));
      }
      return;
    }

    if (primaryRole === role) {
      if (additionalRoles.length > 0) {
        const [nextPrimary, ...rest] = additionalRoles;
        setPrimaryRole(nextPrimary);
        setAdditionalRoles(rest);
      } else {
        setPrimaryRole('');
      }
      return;
    }

    if (additionalRoles.includes(role)) {
      setAdditionalRoles(additionalRoles.filter((r) => r !== role));
    } else {
      setAdditionalRoles([...additionalRoles, role]);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!fullName.trim()) {
      errors.fullName = 'Full name is required.';
    }
    if (!stageName.trim()) {
      errors.stageName = 'Stage name is required.';
    }

    if (!isGoogleUser) {
      if (!email.trim()) {
        errors.email = 'Email address is required.';
      } else if (!validateEmail(email.trim())) {
        errors.email = 'Please enter a valid email address.';
      }

      // Shared Single Source of Truth validation
      const pwdValidation = validatePassword(password, {
        email: email.trim(),
        fullName: fullName.trim(),
        stageName: stageName.trim(),
      });

      if (!pwdValidation.isValid) {
        errors.password = pwdValidation.error || 'Password does not meet all security criteria.';
      }

      if (!confirmPassword) {
        errors.confirmPassword = 'Confirm your password.';
      } else if (password !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match.';
      }
    }

    if (phone.trim() && !validatePhone(phone.trim())) {
      errors.phone = 'Please enter a valid phone number.';
    }

    if (!primaryRole) {
      errors.role = 'Please select your primary profession.';
    }

    if (!agreeTerms) {
      errors.terms = 'You must accept the terms and conditions.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current || loading) {
      return;
    }

    setError('');
    setIsExistingUser(false);

    if (!validateForm()) {
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // 1. Authenticated Google OAuth user completing profile
      if (isGoogleUser) {
        await createProfile(user, {
          fullName: fullName.trim(),
          stageName: stageName.trim(),
          phone: phone.trim() || null,
          city: city.trim() || null,
          primaryRole,
          additionalRoles,
          onboarding_completed: true,
        });
        await completeOnboarding();

        let targetDest = '/dashboard';
        try {
          const savedRedirect = sessionStorage.getItem('auth_redirect_destination');
          if (savedRedirect && savedRedirect !== '/signup') {
            sessionStorage.removeItem('auth_redirect_destination');
            targetDest = savedRedirect;
          }
        } catch {}

        navigate(targetDest, { replace: true });
        return;
      }

      // 2. Pre-check existing email before attempting registration
      try {
        const exists = await checkEmailExists(email.trim().toLowerCase());
        if (exists) {
          setError('An account with this email already exists. Please log in instead.');
          setIsExistingUser(true);
          setLoading(false);
          isSubmittingRef.current = false;
          return;
        }
      } catch (checkErr) {
        console.warn('[SignupPage] Email pre-check non-blocking notice:', checkErr);
      }

      // 3. Register user in Supabase Auth
      const data = await signUp({
        email: email.trim().toLowerCase(),
        password,
        fullName: fullName.trim(),
        stageName: stageName.trim(),
        phone: phone.trim() || null,
        city: city.trim() || null,
        primaryRole,
        additionalRoles,
      });

      if (data?.session) {
        let targetDest = '/dashboard';
        try {
          const savedRedirect = sessionStorage.getItem('auth_redirect_destination');
          if (savedRedirect && savedRedirect !== '/signup') {
            sessionStorage.removeItem('auth_redirect_destination');
            targetDest = savedRedirect;
          }
        } catch {}
        navigate(targetDest, { replace: true });
      } else {
        setUnverifiedEmail(email.trim().toLowerCase());
        setEmailSent(true);
        setShowVerifyModal(true);
      }
    } catch (err) {
      console.error('[SignupPage] Registration error:', err);
      const msg = err.message || 'Registration failed. Please try again.';
      if (
        msg.toLowerCase().includes('already registered') ||
        msg.toLowerCase().includes('already exists') ||
        msg.toLowerCase().includes('user already')
      ) {
        setIsExistingUser(true);
        setError('An account with this email already exists. Please log in instead.');
      } else {
        setError(msg);
      }
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  // Render role card component
  const renderRoleCard = (role) => {
    const isPrimary = primaryRole === role;
    const isAdditional = additionalRoles.includes(role);
    const isSelected = isPrimary || isAdditional;
    const RoleIcon = getRoleIcon(role);

    return (
      <button
        key={role}
        type="button"
        className={`sx-role-card ${isSelected ? 'selected' : ''} ${isPrimary ? 'primary' : ''}`}
        onClick={() => handleRoleClick(role)}
        aria-pressed={isSelected}
        aria-label={`${ROLE_LABELS[role]} ${isPrimary ? '(Primary role)' : isAdditional ? '(Additional role)' : ''}`}
      >
        <span className="sx-role-icon">
          <RoleIcon size={19} color={isSelected ? '#DAF90F' : 'rgba(255,255,255,0.7)'} />
        </span>
        <span className="sx-role-name">{ROLE_LABELS[role]}</span>
        {isPrimary && <span className="sx-role-tag">Primary</span>}
        {isAdditional && <span className="sx-role-tag additional">Added</span>}
      </button>
    );
  };

  return (
    <div className="sx-signup-root">
      <SEO
        title="Create Artist Account | SenritsuX"
        description="Join SenritsuX to distribute your music to Spotify, Apple Music, and 150+ streaming platforms worldwide."
        canonical="/signup"
        noindex={true}
      />

      <main className="sx-signup-container">
        {/* LEFT SECTION: Upper bookmark-style logo and slogan panel */}
        <section className="sx-signup-branding-panel" aria-label="SenritsuX Branding">
          <div className="sx-signup-bookmark-wrapper">
            <img
              src={upperBookmarkLogo}
              alt="SenritsuX - Your Gateway To Be An Artist"
              className="sx-signup-bookmark-img"
              loading="eager"
            />
          </div>
        </section>

        {/* RIGHT SECTION: Dedicated Relative-Positioned Sign Up Sheet Container */}
        <section className="sx-signup-sheet-container" aria-label="Create Account Form Sheet">
          {/* Decorative Illustration: Lower Halftone Boombox with Soundwaves */}
          <div className="sx-sheet-decor-boombox" aria-hidden="true">
            <img
              src={sheetDownElement}
              alt=""
              className="sx-sheet-decor-boombox-img"
            />
          </div>

          {/* COMPACT PASSWORD CHECKER PANEL */}
          {/* Positioned in empty space below left bookmark & left of sign up sheet */}
          <div
            id="sx-password-checklist"
            ref={panelRef}
            className={`sx-password-checker-panel ${showPasswordPanel ? 'visible' : ''}`}
            tabIndex={-1}
            onMouseEnter={() => setIsPanelFocused(true)}
            onMouseLeave={() => setIsPanelFocused(false)}
            onFocus={() => setIsPanelFocused(true)}
            onBlur={(e) => {
              if (panelRef.current && !panelRef.current.contains(e.relatedTarget)) {
                setIsPanelFocused(false);
              }
            }}
            aria-live="polite"
          >
            <div className="sx-pass-panel-header">
              <span className="sx-pass-panel-title">Password Security</span>
              <span className={`sx-pass-strength-pill ${password ? passwordValidation.level.toLowerCase() : 'empty'}`}>
                {password ? passwordValidation.level : 'Required'}
              </span>
            </div>

            {/* Segmented Strength Bar (Never displays Strong while mandatory checks fail) */}
            <div className="sx-pass-strength-track" aria-hidden="true">
              <div className={`sx-pass-strength-segment ${password && passwordValidation.score >= 1 ? `active-${passwordValidation.level.toLowerCase()}` : ''}`} />
              <div className={`sx-pass-strength-segment ${password && passwordValidation.score >= 2 ? `active-${passwordValidation.level.toLowerCase()}` : ''}`} />
              <div className={`sx-pass-strength-segment ${password && passwordValidation.score >= 3 ? `active-${passwordValidation.level.toLowerCase()}` : ''}`} />
              <div className={`sx-pass-strength-segment ${password && passwordValidation.score >= 4 ? `active-${passwordValidation.level.toLowerCase()}` : ''}`} />
            </div>

            <div className="sx-pass-panel-divider" />

            {/* 8 Independent Progressive Criteria Checklist */}
            <ul className="sx-pass-criteria-list">
              {PASSWORD_CRITERIA.map((item) => {
                const isMet = !!password && passwordValidation.requirements[item.key];
                return (
                  <li key={item.key} className={`sx-pass-criterion ${isMet ? 'met' : ''}`}>
                    <span className="sx-pass-criterion-icon">
                      {isMet ? (
                        <Check size={12} color="#DAF90F" strokeWidth={3} />
                      ) : (
                        <span className="sx-pass-dot" />
                      )}
                    </span>
                    <span>{item.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Main Sign Up Card */}
          <div className="sx-signup-card">
            <header className="sx-signup-card-header">
              <h1 className="sx-signup-title">
                {isGoogleUser ? 'Complete Your Profile' : 'Create Account'}
              </h1>
              <p className="sx-signup-subtitle">
                {isGoogleUser ? 'Step 1 - Your artist information' : 'Step 1 - Your basic information'}
              </p>
            </header>

            {/* Error or Alert Banner */}
            {error && (
              <div className="sx-signup-error-banner" role="alert">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                  <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                  <span>{error}</span>
                </div>
                {isExistingUser && (
                  <Link
                    to="/login"
                    state={{ email }}
                    className="sx-login-highlight-link"
                    style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    Log In <ArrowRight size={12} style={{ display: 'inline', marginLeft: 2 }} />
                  </Link>
                )}
              </div>
            )}

            {isGoogleUser && (
              <div className="sx-signup-google-info" role="status">
                <CheckCircle2 size={16} color="#DAF90F" style={{ flexShrink: 0 }} />
                <span>
                  Signed in with Google as <strong>{user.email}</strong>. Complete your creator profile below.
                </span>
              </div>
            )}

            {/* Registration Form */}
            <form onSubmit={handleSignup} noValidate className="sx-signup-form">
              {/* Row 1: Full Name * | Stage Name * | City */}
              <div className="sx-form-row-3">
                <div className="sx-form-field">
                  <label htmlFor="signup-fullname" className="sx-form-label">Full Name *</label>
                  <input
                    id="signup-fullname"
                    type="text"
                    className={`sx-form-input ${fieldErrors.fullName ? 'input-error' : ''}`}
                    placeholder="Your full name"
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      if (fieldErrors.fullName) setFieldErrors((prev) => ({ ...prev, fullName: null }));
                    }}
                    autoComplete="name"
                    disabled={loading}
                    aria-required="true"
                  />
                  {fieldErrors.fullName && <span className="sx-field-error">{fieldErrors.fullName}</span>}
                </div>

                <div className="sx-form-field">
                  <label htmlFor="signup-stagename" className="sx-form-label">Stage Name *</label>
                  <input
                    id="signup-stagename"
                    type="text"
                    className={`sx-form-input ${fieldErrors.stageName ? 'input-error' : ''}`}
                    placeholder="Your artist name"
                    value={stageName}
                    onChange={(e) => {
                      setStageName(e.target.value);
                      if (fieldErrors.stageName) setFieldErrors((prev) => ({ ...prev, stageName: null }));
                    }}
                    disabled={loading}
                    aria-required="true"
                  />
                  {fieldErrors.stageName && <span className="sx-field-error">{fieldErrors.stageName}</span>}
                </div>

                <div className="sx-form-field">
                  <label htmlFor="signup-city" className="sx-form-label">City</label>
                  <input
                    id="signup-city"
                    type="text"
                    className="sx-form-input"
                    placeholder="e.g. Mumbai"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Row 2: Phone | Email * */}
              <div className="sx-form-row-2">
                <div className="sx-form-field">
                  <label htmlFor="signup-phone" className="sx-form-label">Phone</label>
                  <input
                    id="signup-phone"
                    type="tel"
                    className={`sx-form-input ${fieldErrors.phone ? 'input-error' : ''}`}
                    placeholder="+91 XXXXX XXXXX"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      if (fieldErrors.phone) setFieldErrors((prev) => ({ ...prev, phone: null }));
                    }}
                    autoComplete="tel"
                    disabled={loading}
                  />
                  {fieldErrors.phone && <span className="sx-field-error">{fieldErrors.phone}</span>}
                </div>

                <div className="sx-form-field">
                  <label htmlFor="signup-email" className="sx-form-label">
                    Email {isGoogleUser ? '(Google)' : '*'}
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    className={`sx-form-input ${fieldErrors.email || isExistingUser ? 'input-error' : ''}`}
                    style={isGoogleUser ? { opacity: 0.8, cursor: 'not-allowed' } : {}}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      if (isGoogleUser) return;
                      setEmail(e.target.value);
                      if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: null }));
                      if (isExistingUser || error) {
                        setIsExistingUser(false);
                        setError('');
                      }
                    }}
                    autoComplete="email"
                    disabled={isGoogleUser || loading}
                    aria-required="true"
                  />
                  {fieldErrors.email && <span className="sx-field-error">{fieldErrors.email}</span>}
                </div>
              </div>

              {/* Row 3: Password * | Confirm Password * (Only for non-Google users) */}
              {!isGoogleUser && (
                <div className="sx-form-row-passwords">
                  <div className="sx-form-field">
                    <label htmlFor="signup-password" className="sx-form-label">Password *</label>
                    <div className="sx-password-field-wrapper">
                      <input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        className={`sx-form-input ${fieldErrors.password ? 'input-error' : ''}`}
                        placeholder="Min 8 characters"
                        value={password}
                        onChange={(e) => {
                          const newPass = e.target.value;
                          setPassword(newPass);
                          if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: null }));
                          if (confirmPassword && newPass !== confirmPassword) {
                            setFieldErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match.' }));
                          } else if (confirmPassword && newPass === confirmPassword) {
                            setFieldErrors((prev) => ({ ...prev, confirmPassword: null }));
                          }
                        }}
                        onFocus={() => setIsPasswordFocused(true)}
                        onBlur={(e) => {
                          if (panelRef.current && panelRef.current.contains(e.relatedTarget)) {
                            return;
                          }
                          setIsPasswordFocused(false);
                        }}
                        autoComplete="new-password"
                        disabled={loading}
                        aria-required="true"
                        aria-describedby="sx-password-checklist"
                      />
                      <button
                        type="button"
                        className="sx-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        tabIndex={0}
                      >
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {fieldErrors.password && <span className="sx-field-error">{fieldErrors.password}</span>}
                  </div>

                  <div className="sx-form-field">
                    <label htmlFor="signup-confirm-password" className="sx-form-label">Confirm Password *</label>
                    <div className="sx-password-field-wrapper">
                      <input
                        id="signup-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        className={`sx-form-input ${fieldErrors.confirmPassword ? 'input-error' : ''}`}
                        placeholder="Re-enter password"
                        value={confirmPassword}
                        onChange={(e) => {
                          const newConfirm = e.target.value;
                          setConfirmPassword(newConfirm);
                          if (password && newConfirm !== password) {
                            setFieldErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match.' }));
                          } else {
                            setFieldErrors((prev) => ({ ...prev, confirmPassword: null }));
                          }
                        }}
                        autoComplete="new-password"
                        disabled={loading}
                        aria-required="true"
                      />
                      <button
                        type="button"
                        className="sx-password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                        tabIndex={0}
                      >
                        {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {fieldErrors.confirmPassword && (
                      <span className="sx-field-error">{fieldErrors.confirmPassword}</span>
                    )}
                  </div>
                </div>
              )}

              {/* SECTION: What Do You Create? */}
              <section className="sx-profession-section" aria-label="Profession Selection">
                <h2 className="sx-profession-title">What Do You Create?</h2>
                <p className="sx-profession-subtitle">Select your primary profession, then optionally add more.</p>
                <p className="sx-profession-hint">
                  {primaryRole ? 'Tap another role to add it' : 'Tap to select your primary role'}
                </p>

                {/* Profession Grid Row 1 (3 items: Singer, Rapper, Graphic Designer) */}
                <div className="sx-profession-grid-row1">
                  {AVAILABLE_ROLES_ROW_1.map((role) => renderRoleCard(role))}
                </div>

                {/* Profession Grid Row 2 (2 items: Producer, Video Editor) */}
                <div className="sx-profession-grid-row2">
                  {AVAILABLE_ROLES_ROW_2.map((role) => renderRoleCard(role))}
                </div>

                {fieldErrors.role && (
                  <div style={{ textAlign: 'center', marginTop: '0.35rem' }}>
                    <span className="sx-field-error">{fieldErrors.role}</span>
                  </div>
                )}
              </section>

              {/* Terms and Conditions Acceptance */}
              <div className="sx-terms-container">
                <input
                  id="signup-terms"
                  type="checkbox"
                  className="sx-terms-checkbox"
                  checked={agreeTerms}
                  onChange={(e) => {
                    setAgreeTerms(e.target.checked);
                    if (fieldErrors.terms) setFieldErrors((prev) => ({ ...prev, terms: null }));
                  }}
                  disabled={loading}
                  aria-required="true"
                />
                <label htmlFor="signup-terms" className="sx-terms-label">
                  I accept the{' '}
                  <Link to="/terms" className="sx-terms-link" target="_blank" rel="noopener noreferrer">
                    Terms &amp; Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="sx-terms-link" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              {fieldErrors.terms && (
                <div style={{ marginTop: '-0.75rem', marginBottom: '0.75rem', paddingLeft: 28 }}>
                  <span className="sx-field-error">{fieldErrors.terms}</span>
                </div>
              )}

              {/* Bottom Row: Existing Login Link & Create Account Button */}
              <div className="sx-signup-bottom-row">
                <div className="sx-existing-login-text">
                  Already have an account?{' '}
                  <Link to="/login" className="sx-login-highlight-link">
                    Log In
                  </Link>
                </div>

                <button
                  type="submit"
                  className={`sx-signup-submit-btn ${loading ? 'loading' : ''}`}
                  disabled={loading}
                  aria-label="Create Account"
                >
                  {loading ? (
                    <span className="auth-spinner" style={{ width: 18, height: 18 }} />
                  ) : (
                    isGoogleUser ? 'Save Profile' : 'Create Account'
                  )}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>

      {/* Email Verification Modal */}
      {showVerifyModal && (
        <EmailVerificationModal
          isOpen={showVerifyModal}
          email={unverifiedEmail || email}
          onClose={(verified) => {
            setShowVerifyModal(false);
            if (verified) {
              navigate('/dashboard', { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}
